import { apiBase } from './apiBase';
import { apiFetch } from './api';
import { useAuthStore } from '../store/useAuthStore';

// --- Types (mirror backend src/modules/financials/financials.types.ts) ------

interface Envelope<T> {
  success: true;
  data: T;
}

export interface PeriodPnL {
  period: string;
  totalRevenue: number;
  totalExpenses: number;
  profit: number;
  profitMargin: number;
  currency: string;
}

export interface PnLChange {
  revenueChange: number;
  expensesChange: number;
  profitChange: number;
  marginChange: number;
}

export interface FinancialOverview {
  current: PeriodPnL;
  previous?: PeriodPnL;
  change?: PnLChange;
}

export interface CategoryBreakdown {
  category: string;
  amount: number;
}

export interface MonthlyPnL extends PeriodPnL {
  year: number;
  month: number;
  revenueByCategory: CategoryBreakdown[];
  expensesByCategory: CategoryBreakdown[];
}

export interface TrendDataPoint {
  period: string;
  revenue: number;
  expenses: number;
  profit: number;
}

export type TrendRange = '3m' | '6m' | '1y' | 'all';
export type TrendGranularity = 'month' | 'quarter' | 'year';

/** Settlement-channel filter (Transfer/Card/Cash); undefined = all channels. */
export type LedgerPaymentMethod = 'BANK_TRANSFER' | 'CARD' | 'CASH';

export interface FinancialTrends {
  range: TrendRange;
  granularity: TrendGranularity;
  dataPoints: TrendDataPoint[];
  forecast: TrendDataPoint[];
}

export type ExportFormat = 'csv' | 'pdf';
export type ExportReport = 'overview' | 'monthly' | 'trends';

// Dimensional analytics ("Analiză" tab, services nomenclature).
export type AnalyticsDimension =
  | 'firm'
  | 'department'
  | 'service'
  | 'client'
  | 'supplier'
  | 'subscription'
  | 'person';

export interface AnalyticsRow {
  key: string;
  label: string;
  color: string | null;
  income: number;
  expenses: number;
  profit: number;
}

export interface FinancialAnalytics {
  dimension: AnalyticsDimension;
  period: { year: number | null; month: number | null };
  rows: AnalyticsRow[];
  totals: { income: number; expenses: number; profit: number };
  /** Caveat codes, mapped to i18n strings in the UI. */
  notes: string[];
}

export interface OverviewFilters {
  // Multi-select periods: undefined/empty = all years / all months.
  years?: number[];
  months?: number[];
  compare?: boolean;
  method?: LedgerPaymentMethod;
  // Firm multi-select (multi-company): undefined/empty = all firms.
  companyIds?: string[];
}

export interface ExportFilters {
  format: ExportFormat;
  report: ExportReport;
  year?: number;
  month?: number;
  range?: TrendRange;
  companyIds?: string[];
}

function authHeaders(): Record<string, string> {
  const token = useAuthStore.getState().accessToken;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/** CSV form for the `?companyIds=` query param; undefined when empty (= all firms). */
function companyIdsCsv(companyIds?: string[]): string | undefined {
  return companyIds && companyIds.length > 0 ? companyIds.join(',') : undefined;
}

/** CSV form for numeric multi-select params (years/months); undefined when empty. */
function numbersCsv(values?: number[]): string | undefined {
  return values && values.length > 0 ? values.join(',') : undefined;
}

function buildQuery(params: Record<string, string | number | boolean | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '') {
      search.set(key, String(value));
    }
  }
  const qs = search.toString();
  return qs ? `?${qs}` : '';
}

export async function getFinancialOverview(
  filters: OverviewFilters = {},
): Promise<FinancialOverview> {
  const query = buildQuery({
    years: numbersCsv(filters.years),
    months: numbersCsv(filters.months),
    compare: filters.compare,
    method: filters.method,
    companyIds: companyIdsCsv(filters.companyIds),
  });
  const res = await apiFetch<Envelope<FinancialOverview>>(`/api/financials/overview${query}`, {
    headers: authHeaders(),
  });
  return res.data;
}

export async function getMonthlyPnL(
  year: number,
  month: number,
  method?: LedgerPaymentMethod,
  companyIds?: string[],
): Promise<MonthlyPnL> {
  const query = buildQuery({ method, companyIds: companyIdsCsv(companyIds) });
  const res = await apiFetch<Envelope<MonthlyPnL>>(
    `/api/financials/monthly/${year}/${month}${query}`,
    { headers: authHeaders() },
  );
  return res.data;
}

export async function getFinancialTrends(
  range: TrendRange = '1y',
  granularity: TrendGranularity = 'month',
  method?: LedgerPaymentMethod,
  companyIds?: string[],
): Promise<FinancialTrends> {
  const query = buildQuery({ range, granularity, method, companyIds: companyIdsCsv(companyIds) });
  const res = await apiFetch<Envelope<FinancialTrends>>(`/api/financials/trends${query}`, {
    headers: authHeaders(),
  });
  return res.data;
}

export async function fetchFinancialAnalytics(
  dimension: AnalyticsDimension,
  year?: number,
  month?: number,
  method?: LedgerPaymentMethod,
  companyIds?: string[],
): Promise<FinancialAnalytics> {
  const query = buildQuery({
    dimension,
    year,
    month,
    method,
    companyIds: companyIdsCsv(companyIds),
  });
  const res = await apiFetch<Envelope<FinancialAnalytics>>(`/api/financials/analytics${query}`, {
    headers: authHeaders(),
  });
  return res.data;
}

/**
 * POST /api/financials/export and trigger a browser download of the returned
 * CSV/PDF file. Uses raw fetch (not apiFetch) because the response is binary.
 */
export async function exportFinancials(filters: ExportFilters): Promise<void> {
  const apiUrl = apiBase();
  const res = await fetch(`${apiUrl}/api/financials/export`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify(filters),
  });

  if (!res.ok) {
    throw new Error(`Export failed: ${res.status}`);
  }

  const blob = await res.blob();
  const disposition = res.headers.get('Content-Disposition') ?? '';
  const match = /filename="?([^"]+)"?/.exec(disposition);
  const filename = match?.[1] ?? `financials.${filters.format}`;

  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}
